import { envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
    isTrialVersionEnabled,
} from '@thoughtspot/feature-service';
import { translate, useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { getValue } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useBoolean } from 'react-use';
import { useAppContext } from '/@contexts/global-app-context';
import { BlinkErrorBoundary } from '/@utils/error-handling/blink-error-boundary';
import { InviteUsersButtonProps } from '../../invite-users/invite-users.interface';
import { InviteUsersContainer } from '../../invite-users/invite-users/invite-users.container';
import { InviteUsersModalV2Container } from '../invite-users-modal-v2/invite-users-modal-v2.container';
import { UserLimitWarningModal } from '../user-limit-warning/user-limit-warning-modal';

export const InviteUsersButtonContainer: React.FC<InviteUsersButtonProps> = ({
    buttonText = translate('homehub.task.finish.invite'),
    buttonIcon = IconID.ADD_USER,
    isFullWidth = false,
    buttonType = 'secondary',
    buttonClickFn = _.noop,
    onEmailAdd,
    shouldCollectInviteBtnEvent,
    onInviteBtnClickUserAction,
    onInviteComplete = _.noop,
    updateToastAlert,
    isInviteLinkCall = false,
    source,
}) => {
    const { t } = useTranslation();
    const { metricsService, sessionService } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useBoolean(false);
    const [showInviteV2, setShowInviteV2] = useBoolean(false);
    const [showUserLimitWarning, setShowUserLimitWarning] = useBoolean(false);

    const inviteV2NewFeatureFlag = Boolean(
        isFeatureEnabled(FEATURE_FLAG_NAMES.INVITE_USERS_V2_FREE_TRIAL, {
            urlParamValue: envFlags.getValue(
                FEATURE_FLAG_NAMES.INVITE_USERS_V2_FREE_TRIAL,
            ),
        }),
    );
    const isShareStepEnabled = Boolean(
        isFeatureEnabled(
            FEATURE_FLAG_NAMES.INVITE_USERS_V2_FREE_TRIAL_SHARE_STEP,
            {
                urlParamValue: envFlags.getValue(
                    FEATURE_FLAG_NAMES.INVITE_USERS_V2_FREE_TRIAL_SHARE_STEP,
                ),
            },
        ),
    );

    const isInviteTeammatesEnabled =
        isTrialVersionEnabled() || sessionService.isTeamsEnabled();

    const userLimit =
        sessionService.getTeamsConfig() &&
        sessionService.getTeamsConfig().maxUserInTeamEditionOrg
            ? sessionService.getTeamsConfig().maxUserInTeamEditionOrg
            : 5;
    const currentUsers = sessionService.getNumUsersInOrg();

    const showOverLimitModal =
        currentUsers >= userLimit &&
        sessionService.userLimitAlertEnabledForTE();

    const onButtonClick = () => {
        if (shouldCollectInviteBtnEvent) {
            metricsService.collectEvent(onInviteBtnClickUserAction, {
                leftPanelTabName: 'invite-button',
            });
        }
        if (inviteV2NewFeatureFlag && !sessionService.isCopilotEnabled()) {
            setShowInviteV2(true);
        } else {
            buttonClickFn();
            setIsModalOpen(true);
        }
    };

    const checkUserLimit = () => {
        if (showOverLimitModal) {
            setShowUserLimitWarning(true);
        } else {
            onButtonClick();
        }
    };

    useEffect(() => {
        if (isInviteLinkCall) {
            checkUserLimit();
        }
    }, [isInviteLinkCall]);

    const setToastVisible = () => {
        updateToastAlert({
            message: showInviteV2
                ? t('invite.modal.toast.success')
                : t('homehub.invite_others.success_message'),
            isVisible: true,
            onClose: () => {
                updateToastAlert({
                    isVisible: false,
                    message: '',
                });
            },
        });
    };

    return (
        <>
            {!isInviteLinkCall && isInviteTeammatesEnabled && (
                <Button
                    icon={buttonIcon}
                    text={buttonText}
                    onClick={checkUserLimit}
                    type={buttonType}
                    isFullWidth={isFullWidth}
                    data-testid="invite-teammates"
                />
            )}
            {showUserLimitWarning && (
                <UserLimitWarningModal
                    onConfirm={() => {
                        setShowUserLimitWarning(false);
                        onButtonClick();
                    }}
                    onDismiss={() => {
                        setShowUserLimitWarning(false);
                        onInviteComplete();
                    }}
                    userLimit={userLimit}
                    currentUsers={currentUsers}
                />
            )}
            {showInviteV2 ? (
                <BlinkErrorBoundary handledErrors={['*']}>
                    <InviteUsersModalV2Container
                        onModalCloseCb={() => {
                            setShowInviteV2(false);
                            onInviteComplete();
                        }}
                        setIsToastVisible={setToastVisible}
                        isShareStepEnabled={isShareStepEnabled}
                        source={source}
                    />
                </BlinkErrorBoundary>
            ) : (
                <InviteUsersContainer
                    showModals={isModalOpen}
                    onEmailAdd={onEmailAdd}
                    onInviteComplete={() => {
                        onInviteComplete();
                        setIsModalOpen(false);
                        setToastVisible();
                    }}
                    onModalCloseCB={() => {
                        setIsModalOpen(false);
                        onInviteComplete();
                    }}
                    isCopilotEnabled={sessionService.isCopilotEnabled()}
                    source={source}
                />
            )}
        </>
    );
};

export default InviteUsersButtonContainer;
